import { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import format from "@shared/formatters";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import FlatPercent from "@shared/v2/InlineEditingInput/FlatPercent";
import Tooltip from "@shared/v2/Tooltip";
import {
  ArrowRightSolidV6,
  BullseyeArrowRegularV6,
  ChevronDownSolidV6,
  CircleInfoSolidV6,
} from "@shared/v2/Icomoon";
import SaveProgressButton from "./SaveProgressButton";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";
import { useWizardContext } from "./WizardContext";
import { pickRefineStats, saveScopedWizardValue } from "../../services/wizardStorage";

const LabeledFlat = ({ className = "", label, type = "percent", ...props }) => (
  <div className={`tw-flex tw-justify-between tw-items-center tw-gap-[8px] tw-min-h-[36px] ${className}`}>
    <Typography variant="subtitle-s" className="!tw-text-gray-50">
      {label}
    </Typography>
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <FlatPercent showToggle={false} placeholder={" "} flatOrPercent={type} {...props} />
  </div>
);

LabeledFlat.propTypes = {
  className: PropTypes.string,
  label: PropTypes.node.isRequired,
  type: PropTypes.oneOf(["flat", "percent"]),
};

const RecommendedGoals = () => {
  const { show, year, onClose, wizardData, saveProgress, agentId } = useWizardContext();
  const { isPlace } = useCurrentUser().account;
  const { actions, apiGoals, errors, goals: initialGoals } = wizardData;
  const defaultGoals = apiGoals || initialGoals;
  const [showIncomeGoal, setShowIncomGoal] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const [baseGoals, setBaseGoals] = useState({ ...initialGoals });

  useEffect(() => {
    if (show) setBaseGoals({ ...initialGoals });
  }, [show, initialGoals]);

  const handleClose = () => {
    saveProgress({ nextPage: 1 });
    onClose();
  };

  const onChange = (name) => (value) => {
    const cleanValue = typeof value === "string" ? value.replace(/[^0-9.]/g, "") : value;

    if (
      cleanValue > 0 ||
      /royaltyPercentage|brokerageSplitPercentage|brokerageCap|royaltyCap|sellerCommissionSplit|buyerCommissionSplit/.test(
        name,
      )
    ) {
      const nextGoals = { ...baseGoals, [name]: cleanValue };
      setBaseGoals(nextGoals);
      if (name !== "currentNetIncomeGoal")
        saveScopedWizardValue(agentId, year, "refineStats", pickRefineStats(nextGoals), true);
    }
  };

  const resetDisabled = useMemo(
    () =>
      !Object.entries(baseGoals).some(
        ([key, value]) =>
          (Object.hasOwn(pickRefineStats(defaultGoals), key) ? defaultGoals[key] : initialGoals[key]) !==
          value,
      ),
    [baseGoals, defaultGoals, initialGoals],
  );

  const handleReset = () => {
    setBaseGoals({ ...initialGoals, ...pickRefineStats(defaultGoals) });
    saveScopedWizardValue(agentId, year, "refineStats", pickRefineStats(defaultGoals), false);
  };

  const goals = useMemo(() => {
    const {
      avgCommission,
      avgSalePrice,
      brokerageCap,
      brokerageSplitPercentage,
      buyerCommissionSplit,
      currentNetIncomeGoal,
      pendingToCloseRatio,
      royaltyCap,
      royaltyPercentage,
      sellerCommissionSplit,
      sellerToTotalClosingsRatio,
    } = baseGoals;
    const brokerageSplitPercent = brokerageSplitPercentage / 100;
    const royaltySplitPercent = royaltyPercentage === 0 ? 0 : royaltyPercentage / 100;
    let maxBrokerageCap = currentNetIncomeGoal * brokerageSplitPercent;
    let maxRoyaltyCap = currentNetIncomeGoal * royaltySplitPercent;
    if (brokerageCap > 0 && maxBrokerageCap > brokerageCap) maxBrokerageCap = brokerageCap;
    if (royaltyCap > 0 && maxRoyaltyCap > royaltyCap) maxRoyaltyCap = royaltyCap;
    const averageSalePrice = String(avgSalePrice).replace(",", "");

    const pendingToCloseRatioPercent = pendingToCloseRatio / 100;
    const sellerToTotalClosingRatioPercent = 1 - sellerToTotalClosingsRatio / 100;

    const averageCommission = averageSalePrice * (avgCommission / 100);
    const sellerCommission =
      averageCommission * (1 - sellerCommissionSplit / 100) * sellerToTotalClosingRatioPercent;
    const buyerCommission =
      averageCommission * (1 - buyerCommissionSplit / 100) * (1 - sellerToTotalClosingRatioPercent);

    const closedUnits = Math.ceil(
      (Number(currentNetIncomeGoal) + Number(maxBrokerageCap) + Number(maxRoyaltyCap)) /
        (sellerCommission + buyerCommission),
    );
    const pendedUnits = Math.ceil(closedUnits / pendingToCloseRatioPercent);
    const listedUnits = Math.ceil(pendedUnits * (1 - sellerToTotalClosingRatioPercent));
    const closedVolume = closedUnits * averageSalePrice;

    return { closedVolume, closedUnits, pendedUnits, listedUnits };
  }, [baseGoals]);

  const handleSaveProgress = () => {
    const goalsToSave = { ...baseGoals, ...goals };
    actions?.setLocalGoals(goalsToSave);
    saveProgress({ nextGoals: goalsToSave });
  };

  const handleNext = () => {
    const goalsToSave = { ...baseGoals, ...goals };
    actions?.setLocalGoals(goalsToSave);
    saveProgress({ nextPage: 4, nextGoals: goalsToSave });
  };

  const textClassName = isPlace ? "!tw-text-black" : "!tw-text-gray-99";

  return (
    <>
      <Modal.Header
        closeButton
        title={
          <>
            {`${year} Business Planning`} <span className="tw-text-12d tw-text-gray-50">Step 1 of 5</span>
          </>
        }
      />
      <Modal.Body className="tw-flex-1 tw-overflow-auto tw-relative tw-flex tw-flex-col tw-gap-[32px]">
        {errors.global && <Alert variant="error" text={errors.global} />}
        <div className="tw-flex tw-gap-[16px]">
          <div className="tw-flex-1 tw-basis-[33%] tw-p-[16px] tw-flex tw-gap-[16px] tw-items-center tw-border-[1px] tw-border-solid tw-border-neutral-gray-15 tw-rounded-[4px]">
            <BullseyeArrowRegularV6 size="xxl" />
            <div className="tw-flex tw-flex-col">
              <Typography variant="subtitle-m">Net Income Goal</Typography>
              <div className="tw-flex tw-items-center tw-gap-[6px] tw-min-h-[36px]">
                {showIncomeGoal && (
                  <Typography component="span" variant="headline2" className={textClassName}>
                    {format.currency(baseGoals.currentNetIncomeGoal, 0)}
                  </Typography>
                )}{" "}
                <FlatPercent
                  flatOrPercent="flat"
                  placeholder=" "
                  showToggle={false}
                  onInteract={() => setShowIncomGoal((o) => !o)}
                  onCancelEditing={() => setShowIncomGoal((o) => !o)}
                  onConfirmEditing={() => setShowIncomGoal((o) => !o)}
                  // eslint-disable-next-line react/no-unstable-nested-components
                  displayComponent={(handleInteract) => (
                    <TextButton onClick={handleInteract}>Change</TextButton>
                  )}
                  onChange={onChange("currentNetIncomeGoal")}
                />
              </div>
            </div>
          </div>
          <div className="tw-flex-1 tw-basis-[33%] tw-flex tw-flex-col tw-gap-[12px]">
            <Typography>
              List{" "}
              <Typography component="span" variant="headline2" className={textClassName}>
                {goals.listedUnits}
              </Typography>{" "}
              units on market
            </Typography>
            <Typography>
              Pend{" "}
              <Typography component="span" variant="headline2" className={textClassName}>
                {goals.pendedUnits}
              </Typography>{" "}
              units under contract
            </Typography>
            <Typography>
              Close on at least{" "}
              <Typography component="span" variant="headline2" className={textClassName}>
                {goals.closedUnits}
              </Typography>{" "}
              of those units
            </Typography>
            <Typography>
              Close{" "}
              <Typography component="span" variant="headline2" className={textClassName}>
                {format.currency(goals.closedVolume, 0)}
              </Typography>{" "}
              of total volume
            </Typography>
          </div>
        </div>
        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          <div className="tw-flex tw-flex-col tw-gap-[4px]">
            <Typography variant="subtitle-m-bold">Refine Stats</Typography>
            <Typography variant="subtitle-s" className="!tw-font-normal">
              These stats determine your Production Goals above. <b>Click the numbers</b> to adjust your stats
              from the defaults provided. Defaults can be changed by team admins.
            </Typography>
          </div>
          <div className="tw-flex tw-gap-[24px]">
            <div className="tw-flex-1 tw-flex tw-flex-col">
              <div className="tw-flex tw-justify-between tw-items-center tw-border-solid tw-border-[1px] tw-border-t-0 tw-border-x-0 tw-border-gray-10 tw-pb-[12px] tw-mb-[6px]">
                <Typography variant="subtitle-s">Production Averages</Typography>
                <TextButton
                  schema="sentence"
                  size="small"
                  onClick={() => window.open("/agent_stats?report_tab=agentDashboard")}
                >
                  View Agent Dashboard
                </TextButton>
              </div>
              <LabeledFlat
                type="flat"
                label="Avg. Sale Price"
                value={format.number(baseGoals.avgSalePrice, 0)}
                onChange={onChange("avgSalePrice")}
              />
              <LabeledFlat
                className="tw-bg-gray-5"
                label="Avg. Commission"
                value={baseGoals.avgCommission}
                onChange={onChange("avgCommission")}
              />
              <LabeledFlat
                label="Pending to Closed Ratio"
                value={baseGoals.pendingToCloseRatio}
                onChange={onChange("pendingToCloseRatio")}
              />
              <LabeledFlat
                className="tw-bg-gray-5"
                label={
                  <span>
                    Seller to Total Closings Ratio{" "}
                    <Tooltip
                      trigger={<CircleInfoSolidV6 />}
                      content="The percentage of buyer and seller closings that were sellers"
                      innerClassName="tw-max-w-[230px]"
                    />
                  </span>
                }
                value={baseGoals.sellerToTotalClosingsRatio}
                onChange={onChange("sellerToTotalClosingsRatio")}
              />
            </div>
            <div className="tw-flex-1 tw-flex tw-flex-col">
              <div className="tw-flex tw-justify-between tw-items-center tw-border-solid tw-border-[1px] tw-border-t-0 tw-border-x-0 tw-border-gray-10 tw-pb-[12px] tw-mb-[6px]">
                <Typography variant="subtitle-s">Splits & Caps</Typography>
              </div>
              <LabeledFlat
                label="Brokerage Split"
                value={baseGoals.brokerageSplitPercentage}
                onChange={onChange("brokerageSplitPercentage")}
              />
              <LabeledFlat
                className="tw-bg-gray-5"
                type="flat"
                label="Brokerage Cap"
                value={format.number(baseGoals.brokerageCap, 0)}
                onChange={onChange("brokerageCap")}
              />
              <LabeledFlat
                label="Royalty Split"
                value={baseGoals.royaltyPercentage}
                onChange={onChange("royaltyPercentage")}
              />
              <LabeledFlat
                className="tw-bg-gray-5"
                type="flat"
                label="Royalty Cap"
                value={format.number(baseGoals.royaltyCap, 0)}
                onChange={onChange("royaltyCap")}
              />
              <TextButton className="tw-self-start tw-ml-[-6px]" onClick={() => setShowMore((more) => !more)}>
                {showMore ? "Hide" : "Additional"} Values{" "}
                <ChevronDownSolidV6
                  size="s"
                  className={`tw-transition-transform ${showMore ? "tw-rotate-180" : ""}`}
                />
              </TextButton>
              {showMore && (
                <>
                  <LabeledFlat
                    label="Seller Commission Split w/ Team"
                    value={baseGoals.sellerCommissionSplit}
                    onChange={onChange("sellerCommissionSplit")}
                  />
                  <LabeledFlat
                    className="tw-bg-gray-5"
                    label="Buyer Commission Split w/ Team"
                    value={baseGoals.buyerCommissionSplit}
                    onChange={onChange("buyerCommissionSplit")}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
        <Button size="medium" schema="tertiary" onClick={handleClose}>
          Cancel
        </Button>
        <div className="tw-flex tw-gap-[12px]">
          <TextButton onClick={handleReset} disabled={resetDisabled}>
            Reset Values
          </TextButton>
          <SaveProgressButton onClick={handleSaveProgress} />
          <Button size="medium" className="tw-inline-flex tw-items-center tw-gap-[6px]" onClick={handleNext}>
            Next <ArrowRightSolidV6 />
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

export default RecommendedGoals;
